/**
 * Application Router Component
 *
 * Defines all routes for the application including:
 * - Public routes (home, login, signup, password recovery)
 * - Protected routes (main app)
 * - MFA Verification route
 *
 * Uses React Router v6 with BrowserRouter for client-side routing.
 *
 * @fileoverview Main application router with protected routes
 *
 * @example
 * <AppRouter />
 */

import React from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { Center, Spinner, VStack, Text, useColorModeValue } from '@chakra-ui/react';

import { AuthProvider } from '../contexts/AuthContext';
import { UserProvider } from '../Context/userContext';
import { SupportModeProvider } from '../shared/support-mode';
import { useAuth } from '../hooks/useAuth';
import { useUserProfile } from '../hooks/useUserProfile';

// Home/Auth pages
import {
  LoginForm,
  SignUpForm,
  PasswordRecoveryForm,
  ResetPasswordForm,
} from '../pages/home';
import { MfaVerifyPage } from '../pages/auth/MfaVerifyPage';
import { NotFoundPage } from '../pages/NotFoundPage';

// Main app - Dashboard
import { AppDashboard } from '../pages/AppDashboard';
import { Home } from '../pages/Home';
import { TransactionDetail } from '../pages/TransactionDetail';
import { AppLayout } from '../components/layout/AppLayout';
import { SignOut } from '../pages/SignOut';
import { CategoriesPage } from '../pages/CategoriesPage';
import { SnapshotPage } from '../pages/SnapshotPage';

import { TenantTransactionsSummaryPage } from '../pages/TenantTransactionsSummaryPage';
import { TenantTransactionsV2Page } from '../pages/TenantTransactionsV2Page';
import { StatementsPage } from '../pages/StatementsPage';

// Tenant user management
import { UsersIndex } from '../pages/users/UsersIndex';

// Email page (public)
import { EmailPage } from '../pages/email';
import { ReconnectPlaidPage } from '../pages/ReconnectPlaidPage';
import { PlaidOAuthReturn } from '../pages/PlaidOAuthReturn';
import { EditProfilePage } from '../pages/EditProfilePage';

// Admin pages
import { AdminRoute } from '../components/routes/AdminRoute';
import {
  AdminLayout,
  AdminDashboard,
  PlaidHealthBoard,
  JobsStatusPage,
  ConnectedAccountsPage,
  TenantsIndex,
  TenantCreate,
  TenantEdit,
  TenantView,
  InvitesIndex,
  InviteSend,
  InviteView,
  AdminUsersIndex,
  AdminUserView,
  AdminUserInvite,
  AdminAiPage,
  PlaidRemovalPage,
} from '../pages/admin';

/**
 * Props for ProtectedRoute component
 */
interface ProtectedRouteProps {
  /** Child components to render when authenticated */
  children: React.ReactNode;
}

/**
 * Protected Route Component
 *
 * Wraps routes that require authentication.
 * Redirects to login page if user is not authenticated.
 *
 * @param props - Component props
 * @returns Protected route content or redirect to login
 *
 * @example
 * <ProtectedRoute>
 *   <Dashboard />
 * </ProtectedRoute>
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading, mfaRequired, checkingMfa } = useAuth();
  const { isAdmin, loading: profileLoading } = useUserProfile();
  const location = useLocation();
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const brandColor = useColorModeValue('brand.secondary', 'brand.secondaryLight');



  // Show spinner on initial load or while MFA check is in progress (sign-in flow).
  // Skip the overlay for background token refreshes (loading=true but user already known).
  if ((loading && !user) || checkingMfa) {
    return (
      <Center minH="100vh" bg={bgColor}>
        <VStack spacing={4}>
          <Spinner size="xl" color={brandColor} thickness="4px" />
          <Text color="gray.500">Loading...</Text>
        </VStack>
      </Center>
    );
  }

  // Redirect to home/login if not authenticated
  if (!loading && !user) {

    // Save the attempted URL for redirecting after login
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Redirect to MFA verification if required
  if (mfaRequired && location.pathname !== '/mfa-verify') {
    return <Navigate to="/mfa-verify" replace />;
  }
  
  // If getting to mfa-verify but not required, go home (passing state so admin redirect knows origin)
  if (!mfaRequired && location.pathname === '/mfa-verify') {
      return <Navigate to="/home" state={{ fromMfa: true }} replace />;
  }

  // Redirect admins to /admin only right after completing MFA
  if (!mfaRequired && !checkingMfa && !profileLoading && isAdmin && location.pathname === '/home' && location.state?.fromMfa) {
    return <Navigate to="/admin" replace />;
  }


  return <>{children}</>;
};

/**
 * Public Route Component
 *
 * Wraps routes that should redirect authenticated users to the app.
 * Used for login/signup pages to redirect already logged-in users.
 *
 * @param props - Component props
 * @returns Public route content or redirect to app
 */
interface PublicRouteProps {
  /** Child components to render when not authenticated */
  children: React.ReactNode;
  /** If true, authenticated users are redirected to /home */
  redirectIfAuth?: boolean;
}

const PublicRoute: React.FC<PublicRouteProps> = ({
  children,
  redirectIfAuth = false,
}) => {
  const { user, loading } = useAuth();
  const { isAdmin, loading: profileLoading } = useUserProfile();
  const location = useLocation();
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const brandColor = useColorModeValue('brand.secondary', 'brand.secondaryLight');

  // Show loading spinner while checking auth state or profile
  if (loading || (redirectIfAuth && user && profileLoading)) {
    return (
      <Center minH="100vh" bg={bgColor}>
        <VStack spacing={4}>
          <Spinner size="xl" color={brandColor} thickness="4px" />
          <Text color="gray.500">Loading...</Text>
        </VStack>
      </Center>
    );
  }

  // Redirect authenticated users based on role
  if (redirectIfAuth && user) {
    // On Plaid OAuth return, the bank redirects to PLAID_REDIRECT_URI with
    // ?oauth_state_id=... appended. Save the original received URL before any
    // route navigation, so ConnectedBanksList can use it as receivedRedirectUri
    // to match what Plaid expects (which may differ if PLAID_REDIRECT_URI changed
    // between when the link token was created and now).
    if (location.search.includes('oauth_state_id')) {
      sessionStorage.setItem('xacto_oauth_received_redirect_uri', window.location.href);
    }
    return <Navigate to={isAdmin ? '/admin' : '/home'} replace />;
  }

  return <>{children}</>;
};

/**
 * Application Routes Component
 *
 * Defines all routes for the application.
 * Must be used within AuthProvider context.
 *
 * Routes:
 * - / : Login form (redirects to /home if authenticated)
 * - /signup : Sign up form (standalone)
 * - /forgot-password : Password recovery form
 * - /reset-password : Reset password form (from email link)
 * - /home : Home page (protected)
 * - /status : Status/dashboard page (protected)
 * - * : Redirect to home
 */
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/"
        element={
          <PublicRoute redirectIfAuth>
            <LoginForm />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute redirectIfAuth>
            <SignUpForm />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <PasswordRecoveryForm />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordForm />
          </PublicRoute>
        }
      />

      {/* MFA Verification */}
      <Route
        path="/mfa-verify"
        element={
          <ProtectedRoute>
            <MfaVerifyPage />
          </ProtectedRoute>
        }
      />

      {/* Plaid OAuth return — minimal handler, no AppLayout */}
      {/* NOTE: NOT wrapped in ProtectedRoute because after bank OAuth redirect,
          session cookies may not be sent (SameSite restriction). 
          PlaidOAuthReturn handles auth internally via localStorage. */}
      <Route
        path="/plaid/oauth-return"
        element={<PlaidOAuthReturn />}
      />

      {/* Email Route — ADMIN only, matching the backend. EmailController
          went from @Public() (an open mail-send relay) to authenticated,
          and then to @Roles(ADMIN): any signed-in user could otherwise
          send arbitrary to/cc/bcc/html through the app's own Resend
          account and DKIM-aligned domain. Guarding only with
          ProtectedRoute here would render the page for every user and
          then 403 on every request it makes. */}
      <Route
        path="/email"
        element={
          <AdminRoute>
            <EmailPage />
          </AdminRoute>
        }
      />

      {/* Protected Routes */}
      {/* Protected Routes wrapped in AppLayout */}
      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route path="/home" element={<Home />} />
        <Route path="/status" element={<AppDashboard />} />
        <Route path="/transactions" element={<TenantTransactionsV2Page />} />
        <Route path="/transactions-summary" element={<TenantTransactionsSummaryPage />} />

        <Route path="/transaction/:id" element={<TransactionDetail />} />
        <Route path="/categories" element={<CategoriesPage />} />
        <Route
          path="/snapshots"
          element={
            <AdminRoute>
              <SnapshotPage />
            </AdminRoute>
          }
        />
        <Route path="/statements" element={<StatementsPage />} />
        <Route path="/reconnect" element={<ReconnectPlaidPage />} />
        <Route path="/profile/edit" element={<EditProfilePage />} />
        {/* Backwards compatibility redirect */}
        <Route path="/app" element={<Navigate to="/status" replace />} />
      </Route>

      <Route
        path="/signout"
        element={<SignOut />}
      />

      {/* Tenant User Management (authorization handled inside component) */}
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <UsersIndex />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="tenants" element={<TenantsIndex />} />
        <Route path="tenants/new" element={<TenantCreate />} />
        <Route path="tenants/:id" element={<TenantView />} />
        <Route path="tenants/:id/edit" element={<TenantEdit />} />
        <Route path="invites" element={<InvitesIndex />} />
        <Route path="invites/new" element={<InviteSend />} />
        <Route path="invites/:id" element={<InviteView />} />
        <Route path="plaid-health" element={<PlaidHealthBoard />} />
        <Route path="jobs-status" element={<JobsStatusPage />} />
        <Route path="connected-accounts" element={<ConnectedAccountsPage />} />
        <Route path="users" element={<AdminUsersIndex />} />
        <Route path="users/invite" element={<AdminUserInvite />} />
        <Route path="users/:id" element={<AdminUserView />} />
        <Route path="ai" element={<AdminAiPage />} />
        <Route path="plaid-removal" element={<PlaidRemovalPage />} />
      </Route>

      {/* Catch-all: any unmatched path gets a real 404 instead of a silent
          bounce to "/", which read as an unexplained session timeout. */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

/**
 * Application Router Component
 *
 * The main router component that wraps the entire application.
 * Provides routing context and authentication context to all routes.
 *
 * @returns Router with authentication context and all routes
 *
 * @example
 * // In main-vite.tsx
 * import { AppRouter } from './router';
 *
 * root.render(
 *   <React.StrictMode>
 *     <AppChakraProvider>
 *       <QuickstartProvider>
 *         <AppRouter />
 *       </QuickstartProvider>
 *     </AppChakraProvider>
 *   </React.StrictMode>
 * );
 */
export const AppRouter: React.FC = () => {
  // The v7_startTransition / v7_relativeSplatPath future flags used to be
  // opted into on BrowserRouter here. In react-router 7 both are the default
  // behaviour and the `future` prop no longer exists, so passing it is a type
  // error. Because they were already enabled under v6, routing behaviour is
  // unchanged by this upgrade.
  return (
    <BrowserRouter>
      <AuthProvider>
        <SupportModeProvider>
          <UserProvider>
            <AppRoutes />
          </UserProvider>
        </SupportModeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default AppRouter;
